import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import { default as React } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import ActionText from '../../core/ActionText';
import AreYouSure from '../../core/AreYouSure';
import BasicLanguageFieldDisplay from '../../core/BasicLanguageFieldDisplay';
import BasicReadField from '../../core/BasicReadField';
import CancelChange from '../../core/CancelChange';
import Errored from '../../core/Errored';
import ExternalResourceLink from '../../core/ExternalResourceLink';
import LiteralConfirmationField from '../../core/LiteralConfirmationField';
import Loading from '../../core/Loading';
import Notification from '../../core/Notification';
import TitledPanel from '../../core/TitledPanel';
import TypedLiteralDialog from '../../core/TypedLiteralDialog';
import Warning from '../../core/Warning';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { globalStyles as styles } from '../../core/style/gstheme';
import { DATATYPES, STATUS_ACCEPTED, STATUS_DRAFT, STATUS_REJECTED } from '../../core/utils/constants';
import { checkResourceEntitlements, computeDatatype, displayDate, makeCancelable } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import { MAX_DEFINITION_LENGTH } from '../constants';
import LanguageField from '../core/LanguageField';
import SchemeChip from '../core/SchemeChip';
import AssociatedProjectDisplay from './AssociatedProjectDisplay';
import DropPreferredPanel from './DropPreferredPanel';
import DropRelationshipsDisplay from './DropRelationshipsDisplay';
//import DropAlternativePanel from './DropAlternativePanel';
import WorkflowResourceManager from '../../core/workflow/WorkflowResourceManager';
import MemberOf from './MemberOf';
import RelationshipAreYouSure from './RelationshipAreYouSure';
import RelationshipsDisplay from './RelationshipsDisplay';
import SkosLabels from './SkosLabels';
import TypeDisplay from './TypeDisplay';
import XLlabelDisplay from './XLlabelDisplay';

const APPROVED = STATUS_ACCEPTED;
const REJECTED = STATUS_REJECTED;

const noteFields =
    [{
        title: "Definitions",
        message: "Definition",
        property: "definition",
        placeholder: "Enter definition text"
    }, {
        title: "Examples",
        message: "Example",
        property: "example",
        placeholder: "Enter example text"
    }, {
        title: "Editorial Notes",
        message: "Editorial Note",
        property: "editorialNote",
        placeholder: "Enter note text"
    }, {
        title: "Scope Notes",
        message: "Scope Note",
        property: "scopeNote",
        placeholder: "Enter note text"
    }, {
        title: "History Notes",
        message: "History Note",
        property: "historyNote",
        placeholder: "Enter note text"
    }, {
        title: "Change Notes",
        message: "Change Note",
        property: "changeNote",
        placeholder: "Enter note text"
    }];

const matchFields =
    [{
        title: "Related",
        property: "relatedMatch"
    }, {
        title: "Exact",
        property: "exactMatch"
    }, {
        title: "Close",
        property: "closeMatch"
    }, {
        title: "Broad",
        property: "broadMatch"
    }, {
        title: "Narrow",
        property: "narrowMatch"
    }];


class ConceptDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoadingLabels: true,
            isLoadingRelations: true,
            warning: "",
            isErrored: null,
            conceptChanged: false,
            areYouSure: false
        };
        this.cancellable1 = null;
        this.cancellable2 = null;
        this.cancellable3 = null;
        this.cancellable4 = null;
        this.cancellable5 = null;
    }

    checkIfSure = () => this.setState({ areYouSure: true });

    onSure = () => {
        this.setState({ areYouSure: false });
        this.props.onDeleteConcept(this.props.data.id);
    };

    onNotSure = () => this.setState({ areYouSure: false, confirmPropertyRemoval: false });

    componentDidMount() {
        this.retrieveXlLabel();
        this.retrieveRelations();
    }

    // Load all XL labels here for speed
    async retrieveXlLabel() {
        let xlPL = this.props.data.xlPrefLabel ? [...this.props.data.xlPrefLabel] : [];
        let xlAL = this.props.data.xlAltLabel ? [...this.props.data.xlAltLabel] : [];
        let xlHL = this.props.data.xlHiddenLabel ? [...this.props.data.xlHiddenLabel] : [];
        // Make sure we dedupe in case of misuse
        const labelResources = [...new Set([...xlPL, ...xlAL, ...xlHL])];
        try {
            if (labelResources.length > 0) {
                const labelLoadOptions = {
                    predicates: [
                        "http://www.w3.org/2008/05/skos-xl#literalForm",
                        "http://www.w3.org/2004/02/skos/core#inScheme"
                    ]
                };
                this.loadData1(labelResources, labelLoadOptions, false)
                    .promise
                    .then(() => {
                        if (this.cancellable1 && this.cancellable1.isCancelled()) return;
                        this.setState({ isLoadingLabels: false });
                    })
                    .catch((e) => {
                        if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
                    });
            }
            else {
                this.setState({ isLoadingLabels: false });
            }
        }
        catch (e) {
            if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
        }
    }

    async retrieveRelations() {
        // We need to do this here because we need to find any relationships which are not loaded
        let relatedMatches = this.props.data.relatedMatch ? [...this.props.data.relatedMatch] : [];
        let closeMatches = this.props.data.closeMatch ? [...this.props.data.closeMatch] : [];
        let exactMatches = this.props.data.exactMatch ? [...this.props.data.exactMatch] : [];
        let broadMatches = this.props.data.broadMatch ? [...this.props.data.broadMatch] : [];
        let narrowMatches = this.props.data.narrowMatch ? [...this.props.data.narrowMatch] : [];
        let narrower = this.props.data.narrower && this.props.data.narrower.length < 1000 ? [...this.props.data.narrower] : [];
        let broader = this.props.data.broader ? [...this.props.data.broader] : [];
        let related = this.props.data.related ? [...this.props.data.related] : [];
        const additionalResources = Array.from(new Set([...relatedMatches, ...closeMatches, ...exactMatches, ...broadMatches, ...narrowMatches, ...narrower, ...broader, ...related]));
        // Load additional data if applicable. We do it here to load in one go
        try {
            if (additionalResources.length > 0) {
                const relationLoadOptions = {
                    "predicates": [
                        "http://www.w3.org/2004/02/skos/core#inScheme",
                        "http://www.w3.org/2004/02/skos/core#prefLabel",
                        "http://www.w3.org/2002/07/owl#deprecated",
                        "http://purl.org/dc/terms/title",
                        "http://www.w3.org/2008/05/skos-xl#prefLabel",
                        "http://www.w3.org/2004/02/skos/core#narrower",
                        "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                        "https://grafsync.graphifi.com/schema/gs/status"
                    ],
                    "tests": [
                        {
                            "id": "http://www.w3.org/2008/05/skos-xl#prefLabel",
                            "join": [{
                                "predicate": "http://www.w3.org/2008/05/skos-xl#literalForm"
                            }]
                        }
                    ],
                    "language": this.props.viewLang || this.props.defaultLang
                };
                this.loadData2(additionalResources, relationLoadOptions, true)
                    .promise
                    .then(() => {
                        this.setState({ isLoadingRelations: false });
                    })
                    .catch((e) => {
                        if (this.cancellable2 && !this.cancellable2.isCancelled()) this.setState({ isErrored: e });
                    });
            }
            else {
                this.setState({ isLoadingRelations: false });
            }
        }
        catch (e) {
            if (this.cancellable2 && !this.cancellable2.isCancelled()) this.setState({ isErrored: e });
        }
    }

    loadData1(data, options, entail = true, entailmentOptions) {
        this.cancellable1 = makeCancelable(GS.loadData(data, options, this.props.graph, entail, entailmentOptions));
        return this.cancellable1;
    }

    loadData2(data, options, entail = true, entailmentOptions) {
        this.cancellable2 = makeCancelable(GS.loadData(data, options, this.props.graph, entail, entailmentOptions));
        return this.cancellable2;
    }

    doPath(payload) {
        this.cancellable3 = makeCancelable(GS.path(payload));
        return this.cancellable3;
    }

    checkConcept = async (id, property) => {
        if (this.props.id === id) return "You cannot add relations where a concept is related to itself.";
        if (this.props.data.related && this.props.data.related.includes(id)) {
            return `You cannot add a ${property} relationship for a concept that is already related.`;
        }
        if (this.props.data.broader && this.props.data.broader.includes(id)) {
            return `You cannot add a ${property} relationship for a concept that is already broader.`;
        }
        if (this.props.data.narrower && this.props.data.narrower.includes(id)) {
            return `You cannot add a ${property} relationship for a concept that is already narrower.`;
        }
        let isAlreadyBroader = false;
        let isAlreadyNarrower = false;
        const broader = await this.doPath({
            graph: this.props.graph,
            source: this.props.id,
            path: "http://www.w3.org/2004/02/skos/core#broader"
        }).promise;
        if (this.cancellable3 && this.cancellable3.isCancelled()) return null;
        const narrower = await this.doPath({
            graph: this.props.graph,
            source: this.props.id,
            path: "http://www.w3.org/2004/02/skos/core#narrower"
        }).promise;
        if (this.cancellable3 && this.cancellable3.isCancelled()) return null;
        if (broader.results) {
            isAlreadyBroader = broader.results.some(result => result.parent === id);
        }
        if (narrower.results) {
            isAlreadyNarrower = narrower.results.some(result => result.parent === id);
        }
        if (property === "narrower" && isAlreadyBroader) return "You cannot add a concept as narrower that is already broader as this will introduce a circular issue in the hierarchy.";
        if (property === "broader" && isAlreadyNarrower) return "You cannot add a concept as broader that is already narrower as this will introduce a circular issue in the hierarchy.";
        return null;
    };

    doSearch(payload) {
        this.cancellable4 = makeCancelable(GS.search(payload));
        return this.cancellable4;
    }

    checkLabel = async (value, lang) => {
        const data = await this.doSearch(
            {
                graph: [this.props.graph],
                search: [
                    {
                        predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                        value: this.props.schemeIRI,
                    },
                    {
                        predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                        value: "http://www.w3.org/2004/02/skos/core#Concept"
                    },
                    {
                        predicate: [
                            "http://www.w3.org/2004/02/skos/core#prefLabel"
                        ],
                        value: value,
                        isExactMatch: false
                    }
                ],
                language: lang,
                predicates: [
                    "http://www.w3.org/2004/02/skos/core#prefLabel"
                ],
                entailment: ["propertyChain"]
            }
        ).promise;
        if (this.cancellable4 && this.cancellable4.isCancelled()) return null;
        const isDuplicate = data.results ?
            data.results.some(result => {
                // Just in case there are multiple pref labels
                const labels = Array.isArray(result.prefLabel) ?
                    result.prefLabel
                    :
                    [result.prefLabel];
                return labels.some(label => label[lang].toLowerCase() === value.toLowerCase());
            })
            :
            false;
        return isDuplicate;
    };

    mergeConcept = async () => {
        /*const data = this.props.data;
        const defaultLang = this.props.defaultLang;
        // If already happened ignore
        if (data.altLabel && data.altLabel[defaultLang] && data.altLabel[defaultLang].includes(label)) return;
        // Are we merging with plain label or XL label?
        // Just check that the id resource is loaded - it must be though!
        if (!GS.isLoaded(id, this.props.graph)) return false;
        const mergeData = GS.data(id, this.props.graph);
        // If the concept being merged uses a label for default preferred label find it
        const prefLabelIRI = mergeData.xlPrefLabel && mergeData.xlPrefLabel.find(labelId => {
            if (!GS.isLoaded(labelId, this.props.graph)) return false;
            const prefLabelData = GS.data(labelId, this.props.graph);
            // Is this the correct language?
            if (prefLabelData.literalForm[defaultLang]) return true;
            return false;
        })*/
    };

    promoteAltLabel = async (label, lang) => {
        // Check that is no existing prefLabel with the altLabel
        const isDuplicate = await this.checkLabel(label, lang);
        if (isDuplicate === null) return;
        if (isDuplicate) {
            this.setState({ warning: "This label cannot be promoted because it already exists as a preferred label." });
            return;
        }
        const data = this.props.data;
        // If multiple pref labels in a lang ignore
        if (data.prefLabel && data.prefLabel[lang] && Array.isArray(data.prefLabel[lang])) return;
        if (data.altLabel && data.altLabel[lang] && data.altLabel[lang].includes(label)) {
            // Are we swapping with annotation or label?
            // Because of entailment we check XL first
            const prefLabelIRI = data.xlPrefLabel && data.xlPrefLabel.find(id => {
                if (!GS.isLoaded(id, this.props.graph)) return false;
                const prefLabelData = GS.data(id, this.props.graph);
                // Is this the correct language?
                if (prefLabelData.literalForm[lang]) return true;
                return false;
            });
            if (prefLabelIRI) {
                GS.replace(this.props.id, this.props.graph, "xlPrefLabel", prefLabelIRI, null, null, "xlAltLabel", prefLabelIRI);
                GS.deleteProperty(this.props.id, this.props.graph, "altLabel", label, lang);
                GS.addProperty(this.props.id, this.props.graph, "prefLabel", label, lang);
            }
            else if (data.prefLabel && data.prefLabel[lang]) {
                const prefLabel = data.prefLabel && data.prefLabel[lang] ? data.prefLabel[lang] : null;
                GS.deleteProperty(this.props.id, this.props.graph, "altLabel", label, lang);
                if (prefLabel) {
                    GS.deleteProperty(this.props.id, this.props.graph, "prefLabel", prefLabel, lang);
                }
                GS.addProperty(this.props.id, this.props.graph, "prefLabel", label, lang);
                if (prefLabel) {
                    GS.addProperty(this.props.id, this.props.graph, "altLabel", prefLabel, lang);
                }
            }
            else {
                // If prefLabel doesn't exist then just promote the alt label
                GS.deleteProperty(this.props.id, this.props.graph, "altLabel", label, lang);
                GS.addProperty(this.props.id, this.props.graph, "prefLabel", label, lang);
            }
        }
    };

    promoteSKOSaltLabel = async (labelIRI, labelLang) => {
        const data = this.props.data;
        if (data.xlAltLabel && data.xlAltLabel.includes(labelIRI)) {
            // Check that is no existing prefLabel with the altLabel
            if (!GS.isLoaded(labelIRI, this.props.graph)) return;
            const altLabelData = GS.data(labelIRI, this.props.graph);
            const isDuplicate = await this.checkLabel(altLabelData.literalForm[labelLang], labelLang);
            if (isDuplicate === null) return;
            if (isDuplicate) {
                this.setState({ warning: "This label cannot be promoted because it already exists as a preferred label." });
                return;
            }
            // Are we swapping with annotation or label?
            // Because of entailment we check xl first
            const prefLabelIRI = data.xlPrefLabel && data.xlPrefLabel.find(id => {
                if (!GS.isLoaded(id, this.props.graph)) return false;
                const prefLabelData = GS.data(id, this.props.graph);
                // Is this the correct language?
                if (prefLabelData.literalForm[labelLang]) return true;
                return false;
            });
            if (prefLabelIRI) {
                GS.deleteProperty(this.props.id, this.props.graph, "xlPrefLabel", prefLabelIRI);
                GS.deleteProperty(this.props.id, this.props.graph, "xlAltLabel", labelIRI);
                GS.addProperty(this.props.id, this.props.graph, "xlPrefLabel", labelIRI);
                GS.addProperty(this.props.id, this.props.graph, "xlAltLabel", prefLabelIRI);
            }
            else if (data.prefLabel[labelLang]) {
                const prefLabel = data.prefLabel && data.prefLabel[labelLang] ? data.prefLabel[labelLang] : null;
                GS.deleteProperty(this.props.id, this.props.graph, "xlAltLabel", labelIRI);
                if (prefLabel) {
                    GS.deleteProperty(this.props.id, this.props.graph, "prefLabel", prefLabel, labelLang);
                }
                GS.addProperty(this.props.id, this.props.graph, "xlPrefLabel", labelIRI);
                if (prefLabel) {
                    GS.addProperty(this.props.id, this.props.graph, "altLabel", prefLabel, labelLang);
                }
            }
            else {
                // No pref label so just promote the alt label
                GS.replace(this.props.id, this.props.graph, "xlAltLabel", labelIRI, null, null, "xlPrefLabel", labelIRI);
            }
        }
    };

    addLabelToConcept = async (conceptIRI, labelIRI, lang) => {
        await GS.loadData([conceptIRI, labelIRI], {}, this.props.graph);
        if (GS.isLoaded(conceptIRI, this.props.graph) && GS.isLoaded(labelIRI, this.props.graph)) {
            const conceptData = GS.data(conceptIRI, this.props.graph);
            if (conceptData.inScheme && conceptData.inScheme.includes(this.props.schemeIRI)) {
                const labelData = GS.data(labelIRI, this.props.graph);
                if (!labelData.literalForm[lang]) return;
                const label = labelData.literalForm[lang];
                if (conceptData.prefLabel && conceptData.prefLabel[lang] && conceptData.prefLabel[lang].includes(label)) {
                    this.setState({ warning: "This concept already has a preferred label with this value in this language." });
                    return;
                }
                if ((!conceptData.altLabel || !conceptData.altLabel[lang] || !conceptData.altLabel[lang].includes(label))
                    && (!conceptData.hiddenLabel || !conceptData.hiddenLabel[lang] || !conceptData.hiddenLabel[lang].includes(label))) {
                    // Remove from this concept
                    GS.deleteProperty(this.props.id, this.props.graph, "xlAltLabel", labelIRI);
                    // Add to target concept
                    GS.addProperty(conceptIRI, this.props.graph, "xlAltLabel", labelIRI);
                }
                else {
                    this.setState({ warning: "This concept already has a label with this value in this language." });
                }
            }
        }
        else {
            this.setState({ warning: "The selected label cannot be added." });
        }
    };

    addToConcept = async (conceptIRI, label, lang) => {
        // If there are any XL labels entailment will load and do the inference
        await GS.loadData([conceptIRI], {}, this.props.graph, true);
        if (GS.isLoaded(conceptIRI, this.props.graph)) {
            const conceptData = GS.data(conceptIRI, this.props.graph);
            if (conceptData.inScheme && conceptData.inScheme.includes(this.props.schemeIRI)) {
                if (conceptData.prefLabel && conceptData.prefLabel[lang] && conceptData.prefLabel[lang].includes(label)) {
                    this.setState({ warning: "This concept already has a preferred label with this value in this language." });
                    return;
                }
                if ((!conceptData.altLabel || !conceptData.altLabel[lang] || !conceptData.altLabel[lang].includes(label))
                    && (!conceptData.hiddenLabel || !conceptData.hiddenLabel[lang] || !conceptData.hiddenLabel[lang].includes(label))) {
                    // Add to target concept
                    GS.addProperty(conceptIRI, this.props.graph, "altLabel", label, lang);
                    // Remove from this concept
                    GS.deleteProperty(this.props.id, this.props.graph, "altLabel", label, lang);
                }
                else {
                    this.setState({ warning: "This concept already has a label with this value in this language." });
                }
            }
        }
    };

    converLabelToConcept = (label, labelIRI, lang) => this.convertToConcept(label, lang, labelIRI);

    doFind(payload) {
        this.cancellable5 = makeCancelable(GS.find(payload));
        return this.cancellable5;
    }

    convertToConcept = async (label, lang, labelIRI) => {
        if (lang !== this.props.defaultLang) {
            this.setState({
                warning: "Only labels in the default language can be promoted to concepts."
            });
            return;
        }
        const slashHash = this.props.useSlashIRI ? (this.props.schemeIRI.endsWith('/') ? '' : '/') : '#';
        const iriID = this.props.useUuidIRI ? uuidv4() : encodeURIComponent(label);
        // Cater for hash in base IRI
        const baseIRI = this.props.schemeIRI.indexOf('#') > 0 ?
            this.props.schemeIRI.substring(0, this.props.schemeIRI.indexOf('#'))
            :
            this.props.schemeIRI;
        const conceptIRI = baseIRI + slashHash + iriID;
        let isDuplicate = false;
        if (labelIRI) {
            const altLabelData = GS.data(labelIRI, this.props.graph);
            isDuplicate = await this.checkLabel(altLabelData.literalForm[lang], lang);
        }
        else {
            isDuplicate = await this.checkLabel(label, lang);
        }
        if (isDuplicate === null) return;
        if (isDuplicate) {
            this.setState({ warning: "This label cannot be promoted because it already exists as a preferred label." });
            return;
        }
        // Try to ensure IRI isn't already being used
        const isFound = await this.doFind({
            value: conceptIRI,
            graph: this.props.graph
        }).promise;
        let exists = isFound.exists;
        if (this.cancellable5 && this.cancellable5.isCancelled()) return;
        // Has it recently been loaded?
        if (!exists) exists = GS.isLoaded(conceptIRI, this.props.graph);
        if (exists) {
            // Somebody beat us to it!
            this.setState({
                warning: "A concept already exists that has an equivalent IRI."
            });
            return;
        }
        const newConcept = {
            id: conceptIRI,
            type: "Concept",
            inScheme: [this.props.schemeIRI]
        };
        if (!labelIRI) newConcept.prefLabel = { [this.props.defaultLang]: label };
        const additionalChannels = [this.props.schemeIRI];
        // Will ultimately replace these two calls with one combined GS call that effectively does do both
        await GS.insertResource(newConcept, conceptIRI, this.props.graph, additionalChannels);
        // Must be loaded for it to be used so no need to preload
        if (labelIRI) {
            GS.addProperty(conceptIRI, this.props.graph, 'xlPrefLabel', labelIRI);
            // And remove existing relationship
            GS.deleteProperty(this.props.id, this.props.graph, "xlAltLabel", labelIRI);
        }
        else {
            // And remove existing relationship
            GS.deleteProperty(this.props.id, this.props.graph, "altLabel", label, lang);
        }
        GS.addProperty(this.props.id, this.props.graph, "narrower", conceptIRI);
    };

    moveConcept = (property) => (id) => {
        if (this.props[property] && this.props[property].includes(id)) return;
        this.checkConcept(id, property)
            .then(result => {
                if (result === null) this.includeExistingConcept(property)(id);
                else {
                    this.setState({ warning: result });
                }
            })
            .catch((e) => {
                this.setState({ isErrored: e });
            });
    };

    componentWillUnmount() {
        if (this.cancellable1) this.cancellable1.cancel();
        if (this.cancellable2) this.cancellable2.cancel();
        if (this.cancellable3) this.cancellable3.cancel();
        if (this.cancellable4) this.cancellable4.cancel();
        if (this.cancellable5) this.cancellable5.cancel();
    }

    // If createXL is true will get created as a new SKOS-XL Label associated with this concept
    addNewLabel = (property) => async (language, label, createAsXL, labelIRI, reservationID) => {
        if (property === "prefLabel") {
            if (this.props.data.prefLabel[language]) {
                this.setState({
                    warning: "Another user had just added a preferred label in the same language that you tried to add. Your edit will not be applied."
                });
                return;
            }
        }
        if (createAsXL) {
            try {
                const newXLlabel = {
                    id: labelIRI,
                    type: "Label",
                    ...(this.props.useWorkflow) && { status: [STATUS_DRAFT] },
                    inScheme: [this.props.schemeIRI],
                    literalForm: { [language]: label }
                };
                const additionalChannels = [this.props.id];
                await GS.insertResource(newXLlabel, labelIRI, this.props.graph, additionalChannels, reservationID);
                const propName = "xl" + property.charAt(0).toUpperCase() + property.slice(1);
                GS.addProperty(this.props.id, this.props.graph, propName, labelIRI);
            }
            catch (e) {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                this.setState({
                    isErrored: true,
                    message: "There was an issue adding new label."
                });
            }
        }
        else if (labelIRI) {
            GS.addProperty(this.props.id, this.props.graph, "xl" + property.charAt(0).toUpperCase() + property.slice(1), labelIRI);
        }
        else GS.addProperty(this.props.id, this.props.graph, property, label, language);
    };

    convertXlLabel = (propertyToReplace, property) => (iriToReplace, label, lang) => {
        GS.replace(this.props.id, this.props.graph, propertyToReplace, iriToReplace, null, null, property, label, lang);
    };

    removeLabel = (property, language, value) => GS.deleteProperty(this.props.id, this.props.graph, property, value, language);

    removeXLlabel = (property) => (iri) => this.props.onDelete(property, iri);

    addNewTypedLiteral = (property, propertyGraphIRI) => (value, datatype = null) => {
        GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    includeExistingConcept = (property, propertyGraphIRI) => async (newIRI) => {
        const { graph, id, move } = this.props;
        // We only need to check if in monohierarchy mode
        const needToCheck = move && (property === "broader" || property === "narrower") ?
            await this.checkIncludeExistingConcept(property, newIRI, id)
            :
            false;
        if (!needToCheck) {
            // We load the concept we are about to use to make sure rendering works okay
            // But if it's external it won't load and that's okay
            GS.loadData([newIRI], {}, graph, true).then(() => {
                GS.addProperty(id, graph, property, newIRI, null, null, propertyGraphIRI);
            });
        }
    };

    onPropertySure = async () => {
        this.setState({ confirmPropertyRemoval: false });
        const property = this.state.confirmProperty;
        const newIRI = this.state.confirmNewIRI;
        const { graph, id, schemeIRI } = this.props;
        let data;
        if (property === "broader") {
            data = this.props.data;
        }
        else {
            // We first need to load the narrower concept - again hopefully loaded
            await GS.loadData([newIRI], {}, graph, true);
            if (GS.isLoaded(newIRI, graph)) {
                data = GS.data(newIRI, graph);
            }
            else {
                this.setState({ warning: "An issue occurred checking relationships." });
                return;
            }
        }
        if (data._version !== this.state.confirmConceptVersion) {
            this.setState({ conceptChanged: true });
            return;
        }
        const existingRels = data["broader"] || [];
        // We load the concept we are about to use to make sure rendering works okay
        // We also load any related concepts to get inScheme data (they should already be loaded) but we do it to be sure
        GS.loadData([newIRI, ...existingRels], {}, graph, true).then(() => {
            GS.addProperty(id, graph, property, newIRI, null, null);
        });
        // We now remove existing inScheme broader relations for this scheme
        existingRels.forEach(item => {
            if (GS.isLoaded(item, graph)) {
                const relData = GS.data(item, graph);
                if (relData.inScheme && relData.inScheme.includes(schemeIRI)) {
                    GS.deleteProperty(data.id, graph, "broader", item);
                }
            }
        });
        if (data.topConceptOf && data.topConceptOf.includes(schemeIRI)) {
            GS.deleteProperty(data.id, graph, "topConceptOf", schemeIRI);
        }
    };

    checkIncludeExistingConcept = async (property, newIRI, id) => {
        const { graph, schemeIRI } = this.props;
        let hasRels = [];
        let data;
        if (property === "broader") {
            data = this.props.data;
        }
        else {
            // We first need to load the narrower concept - again hopefully loaded
            await GS.loadData([newIRI], {}, graph, true);
            if (GS.isLoaded(newIRI, graph)) {
                data = GS.data(newIRI, graph);
            }
            else {
                this.setState({ warning: "An issue occurred checking relationships." });
                return true;
            }
        }
        const existingRels = data["broader"] || [];
        const hasTopConcept = data.topConceptOf && data.topConceptOf.includes(schemeIRI) ? true : false;
        if (existingRels.length === 0 && !hasTopConcept) return false;
        // Load the concepts we need to check = hopefully mostly loaded already
        if (existingRels.length > 0) {
            await GS.loadData(existingRels, {}, graph, true);
            // If we are in move mode we now check if we would remove existing inScheme broader/narrower relations for this scheme
            existingRels.forEach(item => {
                if (GS.isLoaded(item, graph)) {
                    const relData = GS.data(item, graph);
                    if (relData.inScheme && relData.inScheme.includes(schemeIRI)) {
                        hasRels.push(item);
                    }
                }
            });
        }
        if (hasRels || hasTopConcept) {
            this.setState({
                confirmPropertyRemoval: true,
                confirmProperty: property,
                confirmPropertyIRI: property === "broader" ? id : newIRI,
                confirmNewIRI: newIRI,
                confirmIsTopConcept: hasTopConcept,
                confirmConceptList: hasRels,
                confirmConceptVersion: data._version
            });
            return true;
        }
        return false;
    };

    // Phasing this out in terms of onActualUpdate for updates
    onUpdate = (property, lang, datatype, index, propertyGraphIRI) => (value) =>
        this.props.onChange(property, value, lang, datatype, index, propertyGraphIRI);

    onActualUpdate = (property, lang, index, propertyGraphIRI) => (value) => {
        this.props.onUpdate(property, value, lang, index, propertyGraphIRI);
    };

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) =>
        this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    onAddClass = async (value, inherit = false) => {
        this.props.onAdd("type", value);
        if (inherit) {
            try {
                // We need to ensure messages are processed or batch lock will cause rejection
                await GS.waitForEditsToClear();
                await GS.operate("batchupdate", this.props.graph, {
                    action: "addClass",
                    concept: this.props.id,
                    scheme: this.props.schemeIRI,
                    class: value
                });
            }
            catch (e) {
                this.setState({ isErrored: { message: "Cannot perform class operation on tree." } });
            }
        }
    };

    notationUpdate = (index, datatype) => (value) => {
        // Datatype will be supplied if data is in object form
        // Make sure we make a copy of the set object and not a reference
        let valueToUse = value;
        const existingDatatype = datatype ? datatype : computeDatatype(this.props.data.notation[index]);
        // Under certain situation we might need to case the string to a Javascript native type
        if (existingDatatype === DATATYPES.INTEGER || existingDatatype === DATATYPES.DECIMAL) {
            valueToUse = Number(value);
        }
        this.props.onChange("notation", valueToUse, null, null, index);
    };

    notationDelete = (datatype) => (value) => {
        let valueToUse = value;
        // Under certain situation we might need to case the string to a Javascript native type
        if (datatype === DATATYPES.INTEGER || datatype === DATATYPES.DECIMAL) {
            valueToUse = Number(value);
        }
        this.props.onDelete('notation', valueToUse, null, datatype);
    };

    addToCollection = (collectionIRI) => {
        // Collection will already have been loaded in order to select it so no delay caused
        GS.loadData([collectionIRI], {}, this.props.graph).then(() => {
            // We check that somebody else hasn't added it while we've been looking!
            const collection = GS.data(collectionIRI, this.props.graph);
            if ((!collection.memberList || !collection.memberList.includes(this.props.id)))
                GS.insert(collectionIRI, this.props.graph, 'memberList', this.props.id);
        });
    };

    existingLabels = function () {
        const { prefLabel, altLabel, hiddenLabel } = this.props.data;
        const altLangs = altLabel && altLabel !== undefined ? Object.keys(altLabel) : [];
        const prefLangs = prefLabel && prefLabel !== undefined ? Object.keys(prefLabel) : [];
        const hiddenLangs = hiddenLabel && hiddenLabel !== undefined ? Object.keys(hiddenLabel) : [];
        const uniqueLangKeys = new Set([...altLangs, ...prefLangs, ...hiddenLangs]);
        const existingValues = {};
        uniqueLangKeys.forEach(lang => {
            const altValues = altLabel && altLabel[lang] ? altLabel[lang] : [];
            const prefValues = prefLabel && prefLabel[lang] ? prefLabel[lang] : [];
            const hiddenValues = hiddenLabel && hiddenLabel[lang] ? hiddenLabel[lang] : [];
            const altValueList = Array.isArray(altValues) ? altValues : [altValues];
            const prefValueList = Array.isArray(prefValues) ? prefValues : [prefValues];
            const hiddenValueList = Array.isArray(hiddenValues) ? hiddenValues : [hiddenValues];
            const uniqueValues = [...altValueList, ...prefValueList, ...hiddenValueList];
            existingValues[lang] = uniqueValues;
        });
        return existingValues;
    };

    existingRelationships = function () {
        const { exactMatch, closeMatch, relatedMatch, broadMatch, narrowMatch } = this.props.data;
        const related = relatedMatch || [];
        const close = closeMatch || [];
        const exact = exactMatch || [];
        const broad = broadMatch || [];
        const narrow = narrowMatch || [];
        const uniqueRelationships = new Set([...related, ...close, ...exact, ...broad, ...narrow]);
        return uniqueRelationships;
    };

    onExitWarning = () => this.setState({ warning: "" });

    onExitChange = () => this.setState({ conceptChanged: false });

    shouldComponentUpdate() {
        return !GS.isMaterialising();
    }

    render() {

        const { warning, isLoadingLabels, isLoadingRelations, isErrored, areYouSure, confirmPropertyIRI, confirmIsTopConcept,
            confirmPropertyRemoval, confirmConceptList, conceptChanged } = this.state;

        if (isErrored) return <Errored data={isErrored} />;
        if (isLoadingLabels || isLoadingRelations) return <Loading offset='140px' />;

        const { data, id, schemeIRI, propertyTemplates, defaultLang, showLabelDetail, showOntologyProjects, linkedData, useWorkflow,
            graph, viewLang, showFloatingLabel, useXL, hiddenProps, associatedProject, project, entitlements,
            useSlashIRI, useUuidIRI, iriOptions, projectUsers } = this.props;

        const hidden = hiddenProps ?
            hiddenProps.map(prop => prop.substring(36))
            :
            [];

        if (data._notAvailable) return null;

        const { notation, hiddenLabel, prefLabel, altLabel, xlAltLabel, xlPrefLabel, xlHiddenLabel } = data;

        let indexedNotationValues = notation && cloneDeep(notation).map((content, index) => {
            const dt = computeDatatype(content);
            const tc = typeof content === "object";
            const text = tc ?
                content["@value"]
                :
                content;
            let entry = {
                index,
                text,
                updateType: tc && content["type"],
                datatype: tc ?
                    content["type"]
                    :
                    dt || DATATYPES.STRING
            };
            return entry;
        });

        const orderedNotationValues = indexedNotationValues && indexedNotationValues
            .sort((a, b) => {
                const aValue = a.text.toString().toLowerCase();
                const bValue = b.text.toString().toLowerCase();
                if (aValue < bValue) return -1;
                if (aValue > bValue) return 1;
                const aType = a.datatype;
                const bType = b.datatype;
                if (aType < bType) return -1;
                if (aType > bType) return 1;
                return 0;
            });

        if (data.pendingReplacement) return <ActionText offset="100px">This concept has been replaced. Select again to view new data.</ActionText>;

        // When viewing in a collection or floating view there is no scheme so data is not editable
        const isReadOnly = !schemeIRI || checkResourceEntitlements(data, ENTITLEMENTS.EDIT_TAXONOMY)
            || !data.inScheme || !data.inScheme.includes(schemeIRI) || (data.deprecated === true)
            || (useWorkflow && "status" in data && (data.status[0] === APPROVED || data.status[0] === REJECTED));

        const existingValues = this.existingLabels();
        const existingRelationships = this.existingRelationships();
        let resourceClasses = Array.isArray(data.type) ? data.type : [data.type];
        // Some of the classes may be aliased so convert to IRIs
        resourceClasses = resourceClasses.map(rc => GS.getIRIfromAlias(rc) || rc);

        return (
            <div>
                {warning !== "" && <Warning message={warning} title="Action Not Permitted" onExitWarning={this.onExitWarning} />}
                <ProjectConsumer>
                    {({ skosXL, id: projectIRI, projectLang, hasComponent, ontologyProjectAssociation }) => (
                        <form autoComplete="off">
                            <Grid container spacing={1}>
                                {useWorkflow &&
                                    <Grid item xs={12}>
                                        <WorkflowResourceManager
                                            disabled={(!data.inScheme || data.inScheme.length === 0)
                                                || !(data.inScheme.includes(schemeIRI))
                                                || data.deprecated || data.pendingDeletion}
                                            status={data.status}
                                            id={id}
                                            key={id}
                                            schemeIRI={schemeIRI}
                                            entitlements={entitlements}
                                            defaultLang={defaultLang}
                                            assignedUser={data.assignedUser}
                                            assignedNote={data.assignedNote}
                                            rejectedNote={data.rejectedNote}
                                            projectUsers={projectUsers}
                                            project={project}
                                            graph={graph} />
                                    </Grid>
                                }
                                <Grid item xs={12}>
                                    {data.pendingDeletion &&
                                        <Notification error={true} text="This concept is pending deletion." dataTest="pending-deletion" />
                                    }
                                    {data.deprecated &&
                                        <Notification text="This concept is deprecated." dataTest="deprecated" />
                                    }
                                    {(!data.inScheme || data.inScheme.length === 0) &&
                                        <Notification text="This concept is not in any scheme." dataTest="not-in-scheme" />
                                    }
                                    <MemberOf conceptIRI={id} id={projectIRI} graph={graph} defaultLang={defaultLang} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DropPreferredPanel
                                        dataTest="prefLabels"
                                        height="full"
                                        title="Preferred Labels"
                                        id={id}
                                        isReadOnly={isReadOnly}
                                        onMoveAnnotation={this.promoteAltLabel}
                                        onMoveLabel={this.promoteSKOSaltLabel}
                                        actions=
                                        {!isReadOnly && (prefLabel !== undefined &&
                                            projectLang.filter(lang =>
                                                existingValues ? Object.keys(prefLabel).includes(lang) : false).length !== projectLang.length) ?
                                            <LanguageField
                                                optionForXL={skosXL}
                                                useXL={useXL}
                                                langs={projectLang}
                                                onePerLang={true}
                                                iriOptions={iriOptions}
                                                useSlashIRI={useSlashIRI}
                                                useUuidIRI={useUuidIRI}
                                                existingValues={existingValues}
                                                defaultLang={defaultLang}
                                                schemeIRI={schemeIRI}
                                                viewLang={viewLang}
                                                graph={graph}
                                                title="Add Preferred Label"
                                                name="Preferred Label"
                                                placeholder="Enter label text"
                                                textRef="PREF_LABEL"
                                                existingLangs={Object.keys(prefLabel)}
                                                onAddLanguage={this.addNewLabel("prefLabel")} />
                                            :
                                            null
                                        }>
                                        <SkosLabels
                                            labels={data.prefLabel}
                                            name="prefLabel"
                                            xlLabelType="preferred"
                                            schemeIRI={schemeIRI}
                                            existingValues={existingValues}
                                            entailed={data._entailed}
                                            canDeleteIfDefault={false}
                                            projectIRI={projectIRI}
                                            id={id}
                                            onRemoveLabel={this.removeLabel}
                                            defaultLang={defaultLang}
                                            isReadOnly={isReadOnly}
                                            onUpdate={this.onActualUpdate} />
                                        {skosXL && xlPrefLabel &&
                                            <XLlabelDisplay
                                                id={id}
                                                graph={graph}
                                                useWorkflow={useWorkflow}
                                                values={xlPrefLabel}
                                                onConvertXlLabel={this.convertXlLabel("xlPrefLabel", "prefLabel")}
                                                onBreakLink={this.removeXLlabel('xlPrefLabel')}
                                                showFloatingLabel={showFloatingLabel}
                                                showLabelDetail={showLabelDetail}
                                                canBreakIfDefault={false}
                                                schemeIRI={schemeIRI}
                                                isReadOnly={isReadOnly}
                                                defaultLang={defaultLang} />
                                        }
                                    </DropPreferredPanel>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TypeDisplay
                                        textRef="ADD_CLASS"
                                        offerInheritance={this.props.data.narrower && this.props.data.narrower.length > 0}
                                        graph={graph}
                                        classIRIs={GS.getClassIRIs(id, graph)}
                                        ontologyProjectAssociation={ontologyProjectAssociation}
                                        isReadOnly={isReadOnly}
                                        defaultLang={defaultLang}
                                        viewLang={viewLang}
                                        onAddClass={this.onAddClass}
                                        projectIRI={projectIRI}
                                        id={id}
                                        values={data.type} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TitledPanel
                                        dataTest="altLabels"
                                        height="full"
                                        title="Alternative Labels"
                                        onMergeConcept={this.mergeConcept}
                                        id={id}
                                        isReadOnly={isReadOnly}
                                        actions=
                                        {!isReadOnly ?
                                            <LanguageField
                                                optionForXL={skosXL}
                                                useXL={useXL}
                                                langs={projectLang}
                                                onePerLang={false}
                                                iriOptions={iriOptions}
                                                useSlashIRI={useSlashIRI}
                                                useUuidIRI={useUuidIRI}
                                                existingLangs={altLabel && altLabel !== undefined && Object.keys(altLabel)}
                                                checkForDuplicates={false}
                                                viewLang={viewLang}
                                                schemeIRI={schemeIRI}
                                                graph={graph}
                                                defaultLang={defaultLang}
                                                existingValues={existingValues}
                                                name="Alternative Label"
                                                title="Add Alternative Label"
                                                placeholder="Enter label text"
                                                textRef="ALT_LABEL"
                                                onAddLanguage={this.addNewLabel("altLabel")} />
                                            :
                                            null
                                        }>
                                        {data.altLabel &&
                                            <SkosLabels
                                                labels={data.altLabel}
                                                name="altLabel"
                                                draggable={true}
                                                schemeIRI={schemeIRI}
                                                conceptIRI={id}
                                                existingValues={existingValues}
                                                entailed={data._entailed}
                                                projectIRI={projectIRI}
                                                xlLabelType="alternative"
                                                id={id}
                                                onRemoveLabel={this.removeLabel}
                                                defaultLang={defaultLang}
                                                isReadOnly={isReadOnly}
                                                onUpdate={this.onActualUpdate} />
                                        }
                                        {skosXL && xlAltLabel &&
                                            <XLlabelDisplay
                                                draggable={true}
                                                id={id}
                                                graph={graph}
                                                useWorkflow={useWorkflow}
                                                values={xlAltLabel}
                                                onConvertXlLabel={this.convertXlLabel("xlAltLabel", "altLabel")}
                                                onBreakLink={this.removeXLlabel('xlAltLabel')}
                                                showFloatingLabel={showFloatingLabel}
                                                showLabelDetail={showLabelDetail}
                                                isReadOnly={isReadOnly}
                                                schemeIRI={schemeIRI}
                                                defaultLang={defaultLang} />
                                        }
                                    </TitledPanel>
                                </Grid>
                                {
                                    ['narrower', 'broader', 'related'].map((rel, index) =>
                                        <Grid item xs={12} md={6} key={"rel-" + index}>
                                            <DropRelationshipsDisplay
                                                dataTest={rel}
                                                onAddLabelToConcept={this.addLabelToConcept}
                                                onAddToConcept={this.addToConcept}
                                                onMove={this.moveConcept(rel)}
                                                onConvertToConcept={this.convertToConcept}
                                                onConvertLabelToConcept={this.converLabelToConcept}
                                                title={rel.charAt(0).toUpperCase() + rel.slice(1) + " Concepts"}
                                                addTitle={"Find a " + rel.charAt(0).toUpperCase() + rel.slice(1) + " Concept"}
                                                projectIRI={projectIRI}
                                                source={id}
                                                graph={graph}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                components={hasComponent}
                                                useWorkflow={useWorkflow}
                                                existingRelationships={existingRelationships}
                                                schemeIRI={schemeIRI}
                                                property={rel}
                                                values={data[rel]}
                                                isReadOnly={isReadOnly}
                                                includeExistingConcept={this.includeExistingConcept(rel)} />
                                        </Grid>
                                    )
                                }
                                {!hidden.includes("hiddenLabel") &&
                                    <Grid item xs={12} md={6}>
                                        <TitledPanel dataTest="hiddenLabels" height="full" title="Hidden Labels" actions=
                                            {!isReadOnly ?
                                                <LanguageField
                                                    optionForXL={skosXL}
                                                    useXL={useXL}
                                                    langs={projectLang}
                                                    onePerLang={false}
                                                    iriOptions={iriOptions}
                                                    useSlashIRI={useSlashIRI}
                                                    useUuidIRI={useUuidIRI}
                                                    existingLangs={hiddenLabel && hiddenLabel !== undefined && Object.keys(hiddenLabel)}
                                                    defaultLang={defaultLang}
                                                    viewLang={viewLang}
                                                    schemeIRI={schemeIRI}
                                                    graph={graph}
                                                    existingValues={existingValues}
                                                    checkForDuplicates={false}
                                                    title="Add Hidden Label"
                                                    name="Hidden Label"
                                                    placeholder="Enter label text"
                                                    textRef="HIDDEN_LABEL"
                                                    onAddLanguage={this.addNewLabel("hiddenLabel")} />
                                                :
                                                null
                                            }>
                                            {data.hiddenLabel &&
                                                <SkosLabels
                                                    labels={data.hiddenLabel} name="hiddenLabel" schemeIRI={schemeIRI}
                                                    existingValues={existingValues}
                                                    entailed={data._entailed}
                                                    projectIRI={projectIRI}
                                                    id={id}
                                                    xlLabelType="hidden"
                                                    onRemoveLabel={this.removeLabel}
                                                    isReadOnly={isReadOnly}
                                                    onUpdate={this.onActualUpdate} />
                                            }
                                            {skosXL && xlHiddenLabel &&
                                                <XLlabelDisplay
                                                    id={id}
                                                    graph={graph}
                                                    values={xlHiddenLabel}
                                                    useWorkflow={useWorkflow}
                                                    onConvertXlLabel={this.convertXlLabel("xlHiddenLabel", "hiddenLabel")}
                                                    showFloatingLabel={showFloatingLabel}
                                                    showLabelDetail={showLabelDetail}
                                                    schemeIRI={schemeIRI}
                                                    onBreakLink={this.removeXLlabel('xlHiddenLabel')}
                                                    isReadOnly={isReadOnly} defaultLang={defaultLang} />
                                            }
                                        </TitledPanel>
                                    </Grid>
                                }
                                {!hidden.includes("notation") &&
                                    <Grid item xs={12} md={6}>
                                        <TitledPanel dataTest="notation" height="full" title="Notation" actions=
                                            {!isReadOnly ?
                                                <TypedLiteralDialog
                                                    dataTest="add-notation"
                                                    onAddLiteral={this.addNewTypedLiteral("notation")}
                                                    existingValues={indexedNotationValues}
                                                    title="Add Notation"
                                                    placeholder="Enter notation text"
                                                    name="Notation"
                                                    textRef="NOTATION" />
                                                :
                                                null
                                            }>
                                            <Grid container spacing={1}>
                                                {orderedNotationValues && orderedNotationValues.map((value, displayIndex) => {
                                                    const dt = value.datatype;
                                                    const text = value.text;
                                                    return (
                                                        <Grid key={dt + "-" + text} item xs={12}>
                                                            <LiteralConfirmationField
                                                                dataTest={"notation-" + displayIndex}
                                                                displayDatatype={true}
                                                                key={dt + "-" + text}
                                                                margin="dense"
                                                                text={text}
                                                                datatype={dt}
                                                                existingValues={indexedNotationValues}
                                                                canBeDeleted={true}
                                                                onUpdate={this.notationUpdate(value.index, value.updateType)}
                                                                onDelete={this.notationDelete(dt)}
                                                                isReadOnly={isReadOnly} />
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </TitledPanel>
                                    </Grid>
                                }
                                {matchFields.filter(field => !hidden.includes(field.property)).map(field =>
                                    <Grid key={field.title} item xs={12} md={6}>
                                        <RelationshipsDisplay
                                            dataTest={field.property}
                                            title={field.title + " Matches"}
                                            addTitle={"Find a " + field.title.charAt(0).toUpperCase() + field.title.slice(1) + " Match"}
                                            projectIRI={projectIRI}
                                            source={id}
                                            graph={graph}
                                            permitExternal={true}
                                            matching={true}
                                            linkedData={linkedData}
                                            associatedProject={associatedProject}
                                            defaultLang={defaultLang}
                                            viewLang={viewLang}
                                            existingRelationships={existingRelationships}
                                            components={hasComponent}
                                            schemeIRI={schemeIRI}
                                            property={field.property}
                                            values={data[field.property]}
                                            isReadOnly={isReadOnly}
                                            includeExistingConcept={this.includeExistingConcept(field.property)} />
                                    </Grid>
                                )}
                                {noteFields.filter(field => !hidden.includes(field.property)).map(field =>
                                    <Grid key={field.title} item xs={12} md={6}>
                                        <BasicLanguageFieldDisplay
                                            dataTest={field.property}
                                            message={"Add " + field.message}
                                            defaultLang={viewLang || defaultLang}
                                            title={field.title}
                                            property={field.property}
                                            values={data[field.property]}
                                            maxLength={MAX_DEFINITION_LENGTH}
                                            projectLang={projectLang}
                                            placeholder={field.placeholder}
                                            name={field.message}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onActualUpdate}
                                            isReadOnly={isReadOnly}
                                            multiline={true}
                                            onAddLanguage={this.addNewLiteral} />
                                    </Grid>
                                )}
                                {data.inScheme &&
                                    <Grid item xs={12} lg={6}>
                                        <TitledPanel dataTest="inScheme" height="full" title="In Scheme">
                                            {[...data.inScheme].sort().map(sIRI => hasComponent.includes(sIRI) ?
                                                <SchemeChip key={sIRI}
                                                    id={sIRI}
                                                    viewLang={viewLang}
                                                    defaultLang={defaultLang}
                                                    isReadOnly={isReadOnly}
                                                    graph={graph}
                                                    source={id}
                                                    property="inScheme"
                                                    link={
                                                        APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project='
                                                        + projectIRI + "&taxonomy=" + sIRI + "&view=concept&concept=" + id
                                                    } />
                                                :
                                                <ExternalResourceLink
                                                    key={sIRI}
                                                    id={sIRI}
                                                    graph={graph}
                                                    isReadOnly={isReadOnly}
                                                    source={id}
                                                    property="inScheme" />
                                            )}
                                        </TitledPanel>
                                    </Grid>
                                }
                                {data.topConceptOf && !hidden.includes("topConceptOf") &&
                                    <Grid item xs={12} lg={6}>
                                        <TitledPanel dataTest="topConceptOf" height="full" title="Top Concept Of">
                                            {[...data.topConceptOf].sort().map(sIRI => hasComponent.includes(sIRI) ?
                                                <SchemeChip key={sIRI}
                                                    id={sIRI}
                                                    property="topConceptOf"
                                                    viewLang={viewLang}
                                                    defaultLang={defaultLang}
                                                    isReadOnly={isReadOnly}
                                                    graph={graph}
                                                    source={id}
                                                    link={
                                                        APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project='
                                                        + projectIRI + "&taxonomy=" + sIRI + "&view=concept&concept=" + id
                                                    } />
                                                :
                                                <ExternalResourceLink
                                                    key={sIRI}
                                                    id={sIRI}
                                                    graph={graph}
                                                    isReadOnly={isReadOnly}
                                                    source={id}
                                                    property="topConceptOf" />
                                            )}
                                        </TitledPanel>
                                    </Grid>
                                }
                                {data.iri &&
                                    <Grid item xs={12} lg={6}>
                                        <TitledPanel dataTest="iri-history" title="IRI History">
                                            {data.iri.reverse().map((iri, index) =>
                                                index > 0 ?
                                                    <BasicReadField key={index} dataTest="iri-history-item" margin="dense" text={iri} fullWidth />
                                                    :
                                                    null
                                            )}
                                            <BasicReadField dataTest="iri-history-item" margin="dense" text={id} fullWidth />
                                        </TitledPanel>
                                    </Grid>
                                }
                                {(data.created) &&
                                    <Grid item xs={6}>
                                        <TitledPanel dataTest="dateCreated" title="Date Created">
                                            <BasicReadField margin="dense" text={displayDate(data.created)} />
                                        </TitledPanel>
                                    </Grid>
                                }
                                {showOntologyProjects && ontologyProjectAssociation &&
                                    [...ontologyProjectAssociation].sort().map((opa) =>
                                        <AssociatedProjectDisplay
                                            resourceData={data}
                                            graph={graph}
                                            projectIRI={projectIRI}
                                            schemeIRI={schemeIRI}
                                            useWorkflow={useWorkflow}
                                            propertyTemplates={propertyTemplates}
                                            isReadOnly={isReadOnly}
                                            viewLang={viewLang}
                                            projectLang={projectLang}
                                            linkedData={linkedData}
                                            associatedTaxonomyProject={associatedProject}
                                            addNewLiteral={this.addNewLiteral}
                                            addNewTypedLiteral={this.addNewTypedLiteral}
                                            includeExistingResource={this.includeExistingConcept}
                                            components={hasComponent}
                                            resourceClasses={resourceClasses}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onActualUpdate}
                                            key={opa}
                                            id={opa}
                                            defaultLang={defaultLang} />
                                    )
                                }
                            </Grid>
                        </form>
                    )}
                </ProjectConsumer>
                {confirmPropertyRemoval &&
                    <RelationshipAreYouSure
                        graph={graph}
                        source={confirmPropertyIRI}
                        isTopConcept={confirmIsTopConcept}
                        conceptList={confirmConceptList}
                        defaultLang={defaultLang}
                        onSure={this.onPropertySure}
                        onNotSure={this.onNotSure} />
                }
                {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure}
                    message="Do you really want to delete this concept?" />
                }
                {conceptChanged && <CancelChange onExitChange={this.onExitChange}
                    message={[
                        "Somebody else has just edited this concept so it may have affected your intended action.",
                        "Therefore we have not performed the last action.",
                        "Please check if still applicable and redo if so."
                    ]} />
                }
                <div data-test="info-panel-bottom" >&#160;</div>
            </div >
        );
    }
}

ConceptDetail.defaultProps = {
    move: false,
    useWorkflow: false,
    hiddenProps: []
};

ConceptDetail.propTypes = {
    move: PropTypes.bool,
    data: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    onAdd: PropTypes.func,
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    project: PropTypes.string.isRequired,
    projectUsers: PropTypes.array,
    entitlements: PropTypes.array.isRequired,
    showOntologyProjects: PropTypes.bool.isRequired,
    defaultLang: PropTypes.string.isRequired,
    useWorkflow: PropTypes.bool,
    viewLang: PropTypes.string,
    hiddenProps: PropTypes.array,
    propertyTemplates: PropTypes.array,
    graph: PropTypes.string.isRequired,
    schemeIRI: PropTypes.string,
    useSlashIRI: PropTypes.bool,
    useUuidIRI: PropTypes.bool,
    useXL: PropTypes.bool,
    linkedData: PropTypes.array,
    associatedProject: PropTypes.array,
    showFloatingLabel: PropTypes.func.isRequired,
    showLabelDetail: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onDeleteConcept: PropTypes.func.isRequired,
    classes: PropTypes.object,
    iriOptions: PropTypes.object
};

export default withStyles(styles)(withGS(ConceptDetail, ["ALL"], null, {}, true));